import {isEqual, omitBy} from "lodash";
import {Dispatch} from "redux";

import {IStore} from "../../../app/reducers/hybrid_reducer";
import {IRouteState} from "../../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../../app/services/IServices";
import {appendQueryString} from "../../../app/utils/append_query_string";
import {isServer} from "../../../app/utils/read_environment_variables";
import {enable301ResponseState} from "../../../app/utils/request_response_utils/response_state/response_state_actions";
import {UOfferTypes} from "../../utils/constants_offer";
import {getOfferListUrl} from "../url_utils/get_offer_list_url";
import {parseOfferListPath} from "../url_utils/parse_offer_list_path";
import {UMakeKeys, validateOfferListTagWithParams, validateRangeFilterParams} from "../url_utils/tag_utils";

export interface ISearchResultRouteParams {
    type?: UMakeKeys;
    offerType: UOfferTypes;
    location?: string;
    subLocation?: string;
    subSubLocation?: string;
    tag?: string;
}

const normalizeListUrl = (url: string) => {
    try {
        const parsed = new URL(url, "http://localhost");
        return `${parsed.pathname}${parsed.search}`;
    } catch {
        return url;
    }
};

/** 301 only when the target differs from the current request (avoids self-redirect loops). */
const redirectIfChanged = (dispatch: Dispatch, route: IRouteState<ISearchResultRouteParams>, targetUrl: string) => {
    const currentUrl = appendQueryString(route.pathname, route.query);
    if (normalizeListUrl(targetUrl) === normalizeListUrl(currentUrl)) {
        return false;
    }
    dispatch(enable301ResponseState(targetUrl));
    return true;
};

export const redirectConstraintsAtRoute =
    (services: Partial<IServices>, route: IRouteState<ISearchResultRouteParams>) => async (dispatch: Dispatch, getState: () => IStore) => {
        const {page: strPage, model: queryModel, ...restQuery} = route.query;
        const intPage = parseInt(strPage as string, 10);

        const parsed = parseOfferListPath(route.params);
        const modelFromQuery = Array.isArray(queryModel) ? queryModel[0] : queryModel;

        // Legacy ?model= → path segment (requires make in path)
        if (isServer && modelFromQuery && parsed.type) {
            const queryWithoutModel = {...restQuery};
            if (!isNaN(intPage) && intPage === 1) {
                // drop page=1 together with model→path redirect
            } else if (strPage) {
                (queryWithoutModel as Record<string, unknown>).page = strPage;
            }
            const target = getOfferListUrl({
                offerType: route.params.offerType,
                type: parsed.type,
                model: String(modelFromQuery),
                location: parsed.location,
                subLocation: parsed.subLocation,
                tag: route.params.tag as any,
                ...queryWithoutModel
            });
            if (redirectIfChanged(dispatch, route, target)) {
                return false;
            }
        }

        // Redirect if requested url contains page=1.
        if (!isNaN(intPage) && intPage === 1) {
            if (redirectIfChanged(dispatch, route, appendQueryString(route.pathname, restQuery))) {
                return false;
            }
        }

        // Logical params for validators (no raw RR slots that may hold model)
        const currentParams = omitBy(
            {
                offerType: route.params.offerType,
                type: parsed.type || route.params.type,
                tag: route.params.tag,
                ...route.query,
                ...(parsed.model ? {model: parsed.model} : {}),
                location: parsed.location,
                subLocation: parsed.subLocation
            },
            (v) => v === undefined || v === ""
        );
        const validatedParams = validateOfferListTagWithParams(currentParams as any);
        const validatedRangeFilterParams = omitBy(
            validateRangeFilterParams(validatedParams),
            (v) => v === undefined || v === ""
        );

        if (isServer && !isEqual(currentParams, validatedRangeFilterParams)) {
            const target = getOfferListUrl(validatedRangeFilterParams as any);
            if (redirectIfChanged(dispatch, route, target)) {
                return false;
            }
        }

        return true; // continue data-fetcher
    };
